import { api } from "@/lib/apiClient";

export interface OutstandingBalancesSummary {
  hours_amount: number;
  materials_amount: number;
  travel_km_amount: number;
  ohne_projekt_seconds: number;
  ohne_projekt_hours: number;
  ohne_nachweis_seconds: number;
  ohne_nachweis_hours: number;
  ohne_nachweis_amount: number;
  open_hours_seconds: number;
  open_hours: number;
  total_known_amount: number;
  unpriced_hours_count: number;
  unpriced_materials_count: number;
  unpriced_travel_count: number;
  unpriced_ohne_nachweis_count: number;
}

export interface OutstandingBalancesCustomerRow {
  kunde: string;
  hours_amount: number;
  materials_amount: number;
  travel_km_amount: number;
  ohne_projekt_seconds: number;
  ohne_nachweis_seconds: number;
  ohne_nachweis_amount: number;
  open_hours_seconds: number;
  known_amount: number;
}

export interface OutstandingHoursItem {
  source: "timesheet" | "linked_mark" | string;
  timesheet_id: number;
  timesheet_number: number | string | null;
  status: string;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  beschreibung?: string | null;
  kunde: string | null;
  hours: number;
  hero_nr: string | null;
  unit_price: number | null;
  amount: number | null;
  warning: string | null;
}

export interface OutstandingMaterialItem {
  id: number | null;
  timesheet_id: number;
  timesheet_number: number | string | null;
  status: string;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  kunde: string | null;
  label: string;
  quantity: number;
  unit: string | null;
  hero_nr: string | null;
  artikel_id?: number | null;
  unit_price: number | null;
  amount: number | null;
  warning: string | null;
  has_saved_price?: boolean;
  kalkulation_json?: Record<string, unknown> | null;
}

export interface OutstandingMaterialPricingPayload {
  einzelpreis_netto: number;
  kalkulation_json?: Record<string, unknown> | null;
  text?: string | null;
  einheit?: string | null;
}

export interface OutstandingMaterialPricingResult {
  id: number;
  timesheet_id: number;
  text: string | null;
  menge: number | null;
  einheit: string | null;
  hero_nr: string | null;
  artikel_id: number | null;
  einzelpreis_netto: number | null;
  kalkulation_json: Record<string, unknown> | null;
  has_saved_price: boolean;
}

export interface OutstandingTravelItem {
  timesheet_id: number;
  timesheet_number: number | string | null;
  status: string;
  auftrag_id: number | null;
  auftragsnummer: string | null;
  kunde: string | null;
  /** "eighths" = Achtelregelung, "km" = Fahrtkilometer */
  kind?: "eighths" | "km" | string;
  quantity: number;
  unit?: string | null;
  hero_nr: string | null;
  unit_price: number | null;
  amount: number | null;
  warning: string | null;
}

export interface OutstandingOpenHoursItem {
  auftrag_id: number | null;
  projekt: string;
  partner_name: string;
  kunde?: string | null;
  seconds: number;
  hours: number;
  hero_nr?: string | null;
  unit_price?: number | null;
  amount?: number | null;
  warning?: string | null;
}

export interface OutstandingBalancesReport {
  period: { start: string; end: string };
  summary: OutstandingBalancesSummary;
  by_customer: OutstandingBalancesCustomerRow[];
  hours: OutstandingHoursItem[];
  materials: OutstandingMaterialItem[];
  travel_km: OutstandingTravelItem[];
  ohne_projekt: OutstandingOpenHoursItem[];
  ohne_nachweis: OutstandingOpenHoursItem[];
  /** Combined open hours (legacy); prefer ohne_projekt / ohne_nachweis. */
  open_hours: OutstandingOpenHoursItem[];
}

const OPEN_HOUR_RATE_STORAGE_KEY = "auswertungen.aussenstaende.openHourRate";

export function loadOpenHourRate(): number {
  try {
    const raw = localStorage.getItem(OPEN_HOUR_RATE_STORAGE_KEY);
    if (!raw) return 75;
    const value = Number(String(raw).replace(",", "."));
    return Number.isFinite(value) && value >= 0 ? value : 75;
  } catch {
    return 75;
  }
}

export function saveOpenHourRate(rate: number): void {
  try {
    localStorage.setItem(OPEN_HOUR_RATE_STORAGE_KEY, String(rate));
  } catch {
    // ignore quota / private mode
  }
}

export function formatEuro(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "–";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

export function formatHoursDe(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours)) return "–";
  return `${hours.toFixed(2).replace(".", ",")} h`;
}

export async function fetchOutstandingBalancesReport(
  start: string,
  end: string,
  opts?: { hideFullLicense?: boolean },
): Promise<OutstandingBalancesReport> {
  const params = new URLSearchParams({
    start,
    end,
    hide_full_license: String(opts?.hideFullLicense ?? true),
  });
  return api.get<OutstandingBalancesReport>(`/reports/outstanding-balances?${params}`);
}

export async function saveOutstandingMaterialPricing(
  timesheetId: number,
  materialId: number,
  payload: OutstandingMaterialPricingPayload,
): Promise<OutstandingMaterialPricingResult> {
  return api.put<OutstandingMaterialPricingResult>(
    `/timesheets/${timesheetId}/materials/${materialId}/pricing`,
    payload,
  );
}

/** Manual rate applies only to Stunden ohne Projekt; Ohne Nachweis is in total_known_amount. */
export function estimateTotal(
  summary: OutstandingBalancesSummary | null | undefined,
  openHourRate: number,
): number {
  if (!summary) return 0;
  const ohneProjektSeconds =
    summary.ohne_projekt_seconds ??
    Math.max(
      0,
      (summary.open_hours_seconds || 0) - (summary.ohne_nachweis_seconds || 0),
    );
  const ohneProjektAmount =
    Number.isFinite(openHourRate) && openHourRate > 0
      ? (ohneProjektSeconds / 3600) * openHourRate
      : 0;
  return (summary.total_known_amount || 0) + ohneProjektAmount;
}
